import { Button, Modal, ToastProvider, toast } from '@heroui/react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

// region 通知
// 全局通知:HeroUI toast 队列为模块级,可在 React 树外调用(如 query-client)
export const notify = {
  success: (msg: string) => toast.success(msg),
  error: (msg: string) => toast.danger(msg),
  warning: (msg: string) => toast.warning(msg),
  info: (msg: string) => toast.info(msg),
};
// endregion

// region 确认弹窗
interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}

interface ConfirmRequest {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

// 模块级桥:confirm 可在 React 树外调用,由 ConfirmHost 消费渲染
let pushConfirm: ((req: ConfirmRequest) => void) | null = null;

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!pushConfirm) {
      // Host 未挂载时的防御:视为取消
      resolve(false);
      return;
    }
    pushConfirm({ opts, resolve });
  });
}

function ConfirmHost() {
  const [req, setReq] = useState<ConfirmRequest | null>(null);

  useEffect(() => {
    pushConfirm = (next) => {
      setReq((prev) => {
        // 单槽:新请求顶替旧请求,旧请求按取消处理
        prev?.resolve(false);
        return next;
      });
    };
    return () => {
      pushConfirm = null;
    };
  }, []);

  const close = (ok: boolean) => {
    req?.resolve(ok);
    setReq(null);
  };

  if (!req) return null;
  const {
    title,
    body,
    confirmText = '确定',
    cancelText = '取消',
    danger,
  } = req.opts;
  return (
    <Modal
      isOpen
      onOpenChange={(open) => {
        if (!open) close(false);
      }}
    >
      <Modal.Backdrop>
        <Modal.Container size="sm">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>{title}</Modal.Heading>
            </Modal.Header>
            {body ? <Modal.Body>{body}</Modal.Body> : null}
            <Modal.Footer>
              <Button variant="ghost" onPress={() => close(false)}>
                {cancelText}
              </Button>
              <Button
                variant={danger ? 'danger' : 'primary'}
                onPress={() => close(true)}
              >
                {confirmText}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
// endregion

// 根部挂载一次:Toast 出口 + confirm 出口
export function FeedbackHost() {
  return (
    <>
      <ToastProvider placement="bottom" />
      <ConfirmHost />
    </>
  );
}
